import React from 'react';

export interface AnimatedLetterProps {
  char: string;
  animate: boolean;
}

export const AnimatedLetter: React.FC<AnimatedLetterProps> = ({ char }) => {
  return <span className="inline-block whitespace-pre">{char}</span>;
};

export function animateTextChildren(
  children: React.ReactNode,
  animate: boolean,
  maxChars: number,
  counter: { count: number },
): React.ReactNode {
  const processString = (str: string, baseKey: string) => {
    const parts = str.split(/(\s+)/);
    return parts.map((part, partIdx) => {
      if (/^\s+$/.test(part)) {
        counter.count += part.length;
        return (
          <span key={`${baseKey}-space-${partIdx}`} className="inline">
            {part}
          </span>
        );
      }

      return (
        <span key={`${baseKey}-word-${partIdx}`} className="inline-block">
          {part.split('').map((char, charIdx) => {
            const globalIdx = counter.count;
            counter.count += 1;
            const shouldAnimateChar = animate && globalIdx >= maxChars;

            return (
              <AnimatedLetter
                key={`${baseKey}-char-${partIdx}-${charIdx}`}
                char={char}
                animate={shouldAnimateChar}
              />
            );
          })}
        </span>
      );
    });
  };

  const processNode = (
    node: React.ReactNode,
    nodeKey: string,
  ): React.ReactNode => {
    if (typeof node === 'string') {
      return processString(node, nodeKey);
    }

    if (Array.isArray(node)) {
      return node.map((child, index) =>
        processNode(child, `${nodeKey}-${index}`),
      );
    }

    if (React.isValidElement(node)) {
      const childProps = node.props as { children?: React.ReactNode };
      if (childProps && childProps.children) {
        return React.cloneElement(
          node,
          undefined,
          processNode(childProps.children, `${nodeKey}-el`),
        );
      }
    }

    return node;
  };

  return processNode(children, 'root');
}
